import { useTranslation } from 'react-i18next';
import { Package, DollarSign, Calendar, Send, Clock, Layers } from 'lucide-react';
import type { PurchaseOrder } from '../../../../api/queries/purchasing.queries';
import { StatusBadge, WorkflowTimeline } from '../shared';

interface POCardProps {
  po: PurchaseOrder;
  onClick: () => void;
  onSend?: () => void;
  onRecordGRN?: () => void;
}

export default function POCard({
  po,
  onClick,
  onSend,
  onRecordGRN,
}: POCardProps) {
  const { t, i18n } = useTranslation();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysUntilDelivery = () => {
    if (!po.expectedDeliveryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const delivery = new Date(po.expectedDeliveryDate);
    delivery.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((delivery.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntil = getDaysUntilDelivery();

  const workflowSteps = [
    { id: 'pr', label: 'PR', status: 'completed' as const },
    { id: 'po', label: 'PO', status: 'completed' as const },
    {
      id: 'sent',
      label: t('purchasing.sent'),
      status: po.status !== 'draft' ? 'completed' as const : 'pending' as const,
    },
    {
      id: 'grn',
      label: 'GRN',
      status: po.status === 'completed' ? 'completed' as const : 'pending' as const,
    },
  ];

  return (
    <div
      className="rounded-xl border bg-card hover:border-primary/50 hover:shadow-sm transition-all cursor-pointer"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground">{po.poNumber}</h3>
              <StatusBadge status={po.status} />
              {po.isConsolidated && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 text-xs font-medium">
                  <Layers className="h-3 w-3" />
                  {t('purchasing.consolidated')}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {po.supplierId?.name || 'Unknown Supplier'}
              {po.deliveryBranchId && (
                <span> • {typeof po.deliveryBranchId === 'object' ? po.deliveryBranchId.name : ''}</span>
              )}
            </p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-4 flex items-center gap-5 text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Package className="h-4 w-4" />
            {po.items.length} {t('pos.items')}
          </span>
          <span className="flex items-center gap-1.5 font-medium">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            {formatCurrency(po.total)}
          </span>
          {po.expectedDeliveryDate && (
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {formatDate(po.expectedDeliveryDate)}
            </span>
          )}
        </div>

        {/* Days Until Delivery */}
        {daysUntil !== null && po.status !== 'completed' && po.status !== 'cancelled' && (
          <div className="mt-3">
            <span
              className={`text-xs font-medium ${
                daysUntil < 0
                  ? 'text-rose-600 dark:text-rose-400'
                  : daysUntil <= 2
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground'
              }`}
            >
              {daysUntil < 0
                ? `${Math.abs(daysUntil)} ${t('common.days')} ${t('purchasing.overdue')}`
                : daysUntil === 0
                ? t('purchasing.dueToday')
                : `${daysUntil} ${t('common.days')} ${t('purchasing.untilDelivery')}`}
            </span>
          </div>
        )}

        {/* Workflow Progress */}
        {po.status !== 'draft' && po.status !== 'cancelled' && (
          <div className="mt-4 pt-4 border-t">
            <WorkflowTimeline steps={workflowSteps} compact />
          </div>
        )}

        {/* Action Buttons */}
        {(po.status === 'draft' || ['sent', 'confirmed', 'partially_received'].includes(po.status)) && (
          <div className="mt-4 pt-4 border-t flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {po.status === 'draft' && onSend && (
              <button
                onClick={onSend}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Send className="h-4 w-4" />
                {t('purchasing.sendPO')}
              </button>
            )}

            {['sent', 'confirmed', 'partially_received'].includes(po.status) && onRecordGRN && (
              <button
                onClick={onRecordGRN}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border hover:bg-secondary text-sm font-medium transition-colors"
              >
                <Clock className="h-4 w-4" />
                {t('purchasing.recordGRN')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
